import { useQuery } from '@tanstack/react-query'
import { betFetch, hasLiveApi, hasLiveBet } from '@/api/client'
import { toConsoleBet, type AnalyticsServiceBet } from '@/api/map/bets'
import * as api from '@/data/api'
import { keys } from '@/api/hooks/shared/keys'
import type { Paged, Bet } from '@/data/types'

function emptyPage(query: api.BetQuery): Paged<Bet> {
  const page = query.page ?? 1
  const pageSize = query.pageSize ?? 50
  return { rows: [], total: 0, page, pageSize }
}

async function loadBets(query: api.BetQuery): Promise<Paged<Bet>> {
  // Live Keycloak: real bet-service only — never fixtures or invented mock rows.
  if (hasLiveApi()) {
    if (!hasLiveBet() || !query.accountId) return emptyPage(query)
    const live = await betFetch<AnalyticsServiceBet[]>(
      `/v1/members/${query.accountId}/bets`,
    )
    let rows = (live ?? []).map(toConsoleBet)
    // Older bet-service versions ignore marketId; retain exact drill-down
    // semantics client-side until every environment supports the query.
    if (query.marketId) rows = rows.filter((b) => b.marketId === query.marketId)
    if (query.range) {
      const from = query.range.from
      const to = query.range.to
      rows = rows.filter((b) => {
        const day = b.placedAt.slice(0, 10)
        return (!from || day >= from.slice(0, 10)) && (!to || day <= to.slice(0, 10))
      })
    }
    if (query.statuses?.length) {
      rows = rows.filter((b) => query.statuses!.includes(b.status))
    }
    const page = query.page ?? 1
    const pageSize = query.pageSize ?? 50
    const start = (page - 1) * pageSize
    return {
      rows: rows.slice(start, start + pageSize),
      total: rows.length,
      page,
      pageSize,
    }
  }
  return api.listBets(query)
}

export function useBets(query: api.BetQuery) {
  return useQuery({
    queryKey: keys.bets(query),
    queryFn: () => loadBets(query),
    placeholderData: (previous) => previous,
  })
}
